import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Container,
  Link as MuiLink,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import AlternateEmailOutlinedIcon from '@mui/icons-material/AlternateEmailOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { DISPLAY_FONT, PREMIUM_COLORS, tokens } from '../theme';
import { salonApi, Salon, SalonService, PublicBranch, WorkingHours } from '../services/salonService';
import { EmptyState, LoadingState, SectionCard, StatusPill } from '../components/ui';
import { countLabel, formatDuration, formatMoney, initials } from '../utils/format';

const DAY_LABELS = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];
// Tydzien od poniedzialku, jak w polskich kalendarzach.
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0];

const sectionNav = [
  { id: 'oferta', label: 'Usługi i cennik' },
  { id: 'oddzialy', label: 'Oddziały' },
  { id: 'rezerwacja', label: 'Rezerwacja' },
  { id: 'kontakt', label: 'Kontakt' },
  { id: 'godziny', label: 'Godziny otwarcia' },
];

function groupServicesByCategory(services: SalonService[]) {
  const groups = new Map<string, SalonService[]>();
  for (const service of services) {
    const key = service.category || 'Inne usługi';
    const list = groups.get(key) || [];
    list.push(service);
    groups.set(key, list);
  }
  return Array.from(groups.entries());
}

const hoursText = (h: WorkingHours) => (h.isClosed ? 'Zamknięte' : `${h.openTime}–${h.closeTime}`);

const onDark = {
  text: alpha(tokens.paper, 0.8),
  dim: PREMIUM_COLORS.sidebar.text,
};

const SalonLogo: React.FC<{ salon: Salon }> = ({ salon }) => {
  const words = (salon.publicTitle || salon.name || '').split(' ');
  return (
    <Avatar
      variant="rounded"
      src={salon.logoUrl || salon.brand?.logoUrl || undefined}
      alt=""
      sx={{
        width: { xs: 64, md: 84 },
        height: { xs: 64, md: 84 },
        borderRadius: { xs: '16px', md: '20px' },
        bgcolor: tokens.aubergineRaised,
        border: `1px solid ${alpha(tokens.champagne, 0.45)}`,
        color: tokens.champagne,
        fontFamily: DISPLAY_FONT,
        fontWeight: 600,
        fontSize: '1.375rem',
        flexShrink: 0,
        '& img': { objectFit: 'cover' },
      }}
    >
      {initials(words[0], words[1], salon.name)}
    </Avatar>
  );
};

const ContactRow: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <Stack direction="row" gap={1.5} alignItems="flex-start" sx={{ py: 1.25 }}>
    <Box sx={{ color: tokens.muted, display: 'flex', mt: '1px', '& .MuiSvgIcon-root': { fontSize: 20 } }}>{icon}</Box>
    <Typography component="div" sx={{ fontSize: '0.9375rem', minWidth: 0, wordBreak: 'break-word' }}>{children}</Typography>
  </Stack>
);

export const PublicSalonPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [salon, setSalon] = useState<Salon | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!slug) {
        setError('Brak adresu wizytówki.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        const data = await salonApi.getPublicBySlug(slug);
        setSalon(data);
      } catch {
        setError('Nie udało się odnaleźć publicznej strony tego salonu.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [slug]);

  const heroDescription = useMemo(() => {
    if (!salon) return '';
    return salon.publicDescription || salon.description || 'Poznaj ofertę usług i skontaktuj się z salonem.';
  }, [salon]);

  const serviceGroups = useMemo(
    () => groupServicesByCategory(salon?.services || []),
    [salon?.services],
  );

  const visibleSections = useMemo(() => {
    const sections = ['oferta', 'kontakt', 'godziny'];
    if (salon?.branches?.length) sections.push('oddzialy');
    if (salon?.allowOnlineBooking) sections.push('rezerwacja');
    return sectionNav.filter((s) => sections.includes(s.id));
  }, [salon]);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: tokens.ground }} aria-busy="true" aria-label="Ładowanie wizytówki">
        <Box sx={{ bgcolor: tokens.aubergine }}>
          <Container maxWidth="lg" sx={{ pt: { xs: 6, md: 10 }, pb: { xs: 5, md: 7 } }}>
            <Skeleton variant="rounded" width={84} height={84} sx={{ bgcolor: alpha(tokens.paper, 0.08) }} />
            <Skeleton width="50%" height={52} sx={{ mt: 2.5, bgcolor: alpha(tokens.paper, 0.08) }} />
            <Skeleton width="35%" height={24} sx={{ bgcolor: alpha(tokens.paper, 0.08) }} />
          </Container>
        </Box>
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
          <LoadingState variant="table" rows={5} />
        </Container>
      </Box>
    );
  }

  if (error || !salon) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: tokens.ground, py: { xs: 6, md: 12 } }}>
        <Container maxWidth="sm">
          <Paper>
            <EmptyState
              icon={<StorefrontOutlinedIcon />}
              title="Nie możemy wyświetlić tej wizytówki"
              description={`${error || 'Nie znaleziono salonu.'} Sprawdź, czy adres jest poprawny — salon mógł też jeszcze nie opublikować swojej strony.`}
              actions={<Button component={Link} to="/" variant="outlined">Przejdź do strony Visitio</Button>}
            />
          </Paper>
        </Container>
      </Box>
    );
  }

  const title = salon.publicTitle || salon.name;
  const phone = salon.publicPhone || salon.phone;
  const email = salon.publicEmail || salon.email;
  const address = salon.publicAddress || (salon.address ? `${salon.address}, ${salon.city}` : '');
  const canCall = !!(salon.allowPhoneContact && phone);
  const todayIdx = new Date().getDay();
  const today = salon.workingHours?.find((h) => h.dayOfWeek === todayIdx);
  const hoursByDay = WEEK_ORDER.map((d) => salon.workingHours?.find((h) => h.dayOfWeek === d)).filter(Boolean) as WorkingHours[];
  const eyebrow = [salon.brand?.name, salon.city].filter(Boolean).join(' · ');
  const bookingUrl = `/s/${slug}/rezerwacja`;

  return (
    <Box sx={{ bgcolor: tokens.ground, minHeight: '100vh', pb: { xs: salon.allowOnlineBooking ? 12 : 0, md: 0 } }}>
      <Box
        component="header"
        sx={{
          bgcolor: tokens.aubergine,
          color: tokens.paper,
          ...(salon.coverUrl
            ? {
                // Przyciemnienie zdjecia okladki, zeby tekst byl czytelny na kazdym zdjeciu.
                backgroundImage: `linear-gradient(180deg, ${alpha(tokens.aubergine, 0.3)} 0%, ${alpha(tokens.aubergine, 0.88)} 78%), url("${salon.coverUrl.replace(/"/g, '%22')}")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : {}),
        }}
      >
        <Container maxWidth="lg" sx={{ pt: { xs: salon.coverUrl ? 16 : 6, md: salon.coverUrl ? 24 : 10 }, pb: { xs: 4.5, md: 7 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={{ xs: 2.5, md: 3.5 }} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
            <SalonLogo salon={salon} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              {eyebrow && (
                <Typography variant="overline" component="p" sx={{ color: tokens.champagne, lineHeight: 1.5, mb: 0.75 }}>
                  {eyebrow}
                </Typography>
              )}
              <Typography
                variant="h1"
                sx={{ color: tokens.paper, fontSize: { xs: '2rem', md: '2.75rem' }, lineHeight: 1.12, textWrap: 'balance', wordBreak: 'break-word' } as object}
              >
                {title}
              </Typography>
              <Typography sx={{ mt: 1.5, maxWidth: 640, fontSize: { xs: '0.9375rem', md: '1.0625rem' }, lineHeight: 1.65, color: onDark.text }}>
                {heroDescription}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            gap={{ xs: 1, sm: 3 }}
            flexWrap="wrap"
            sx={{ mt: { xs: 3, md: 4 }, color: onDark.dim, '& .MuiSvgIcon-root': { fontSize: 18, color: alpha(tokens.champagne, 0.85) } }}
          >
            {address && (
              <Stack direction="row" gap={1} alignItems="center" sx={{ minWidth: 0 }}>
                <LocationOnOutlinedIcon />
                <Typography sx={{ fontSize: '0.9375rem', color: 'inherit' }}>{address}</Typography>
              </Stack>
            )}
            {today && (
              <Stack direction="row" gap={1} alignItems="center">
                <AccessTimeOutlinedIcon />
                <Typography sx={{ fontSize: '0.9375rem', color: 'inherit', fontVariantNumeric: 'tabular-nums' }}>
                  {today.isClosed ? 'Dziś zamknięte' : `Dziś ${today.openTime}–${today.closeTime}`}
                </Typography>
              </Stack>
            )}
          </Stack>

          {(salon.allowOnlineBooking || canCall) && (
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} sx={{ mt: { xs: 3.5, md: 4.5 } }}>
              {salon.allowOnlineBooking && (
                <Button
                  size="large"
                  variant="contained"
                  startIcon={<EventAvailableOutlinedIcon />}
                  onClick={() => navigate(bookingUrl)}
                  sx={{
                    bgcolor: tokens.paper,
                    color: tokens.plum,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: tokens.plumSoft, boxShadow: 'none' },
                  }}
                >
                  Zarezerwuj wizytę
                </Button>
              )}
              {canCall && (
                <Button
                  size="large"
                  variant="outlined"
                  startIcon={<PhoneOutlinedIcon />}
                  href={`tel:${phone}`}
                  sx={{
                    bgcolor: 'transparent',
                    color: tokens.paper,
                    borderColor: alpha(tokens.paper, 0.3),
                    '&:hover': { bgcolor: alpha(tokens.paper, 0.06), borderColor: alpha(tokens.paper, 0.55) },
                  }}
                >
                  Zadzwoń do salonu
                </Button>
              )}
            </Stack>
          )}
        </Container>
      </Box>

      <Box
        component="nav"
        aria-label="Sekcje wizytówki"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          bgcolor: alpha(tokens.paper, 0.94),
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${tokens.line}`,
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            gap={0.5}
            sx={{ py: 1, overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' }, mx: { xs: -1, sm: 0 } }}
          >
            {visibleSections.map((item) => (
              <Button key={item.id} variant="text" size="small" onClick={() => scrollTo(item.id)} sx={{ color: tokens.inkSoft, flexShrink: 0, '&:hover': { color: tokens.ink } }}>
                {item.label}
              </Button>
            ))}
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" component="main" sx={{ py: { xs: 3, md: 5 } }}>
        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2, md: 3 },
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) 340px', lg: 'minmax(0, 1fr) 380px' },
            alignItems: 'start',
          }}
        >
          <Stack gap={{ xs: 2, md: 3 }} sx={{ minWidth: 0 }}>
            <Box id="oferta" sx={{ scrollMarginTop: 72 }}>
              <Box id="cennik" sx={{ scrollMarginTop: 72 }}>
                <SectionCard
                  title="Usługi i cennik"
                  subtitle={salon.services?.length ? countLabel(salon.services.length, 'usługa', 'usługi', 'usług') : undefined}
                  padding="none"
                >
                  {serviceGroups.length > 0 ? (
                    serviceGroups.map(([category, items], groupIdx) => (
                      <Box key={category} component="section" aria-label={category}>
                        <Typography
                          component="h3"
                          sx={{
                            px: { xs: 2, md: 2.5 },
                            py: 1,
                            bgcolor: tokens.surface,
                            borderTop: groupIdx > 0 ? `1px solid ${tokens.line}` : 'none',
                            borderBottom: `1px solid ${tokens.line}`,
                            fontSize: '0.8125rem',
                            fontWeight: 700,
                            color: tokens.muted,
                          }}
                        >
                          {category}
                        </Typography>
                        {items.map((service) => {
                          const desc = service.publicDescription || service.description;
                          return (
                            <Stack
                              key={service.id}
                              direction="row"
                              gap={2}
                              alignItems="flex-start"
                              sx={{ px: { xs: 2, md: 2.5 }, py: 2, '&:not(:last-of-type)': { borderBottom: `1px solid ${tokens.line}` } }}
                            >
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{ fontWeight: 650, fontSize: '1rem', lineHeight: 1.4 }}>{service.name}</Typography>
                                {desc && (
                                  <Typography sx={{ mt: 0.25, color: tokens.inkSoft, fontSize: '0.875rem' }}>{desc}</Typography>
                                )}
                              </Box>
                              <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '1rem', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                  {formatMoney(service.price)}
                                </Typography>
                                <Typography variant="caption" component="div" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                                  {formatDuration(service.duration)}
                                </Typography>
                              </Box>
                            </Stack>
                          );
                        })}
                      </Box>
                    ))
                  ) : (
                    <EmptyState
                      compact
                      icon={<ContentCutOutlinedIcon />}
                      title="Salon nie opublikował jeszcze cennika"
                      description="Zapytaj o ofertę telefonicznie lub mailowo — dane kontaktowe znajdziesz obok."
                    />
                  )}
                </SectionCard>
              </Box>
            </Box>

            {salon.branches && salon.branches.length > 0 && (
              <Box id="oddzialy" sx={{ scrollMarginTop: 72 }}>
                <SectionCard title="Oddziały" subtitle="Wybierz lokalizację, która jest dla Ciebie najwygodniejsza">
                  <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' } }}>
                    {salon.branches.map((branch: PublicBranch) => (
                      <Box
                        key={branch.id}
                        sx={{
                          p: 2,
                          borderRadius: '12px',
                          border: `1px solid ${branch.isCurrent ? tokens.plum : tokens.line}`,
                          bgcolor: branch.isCurrent ? tokens.plumSofter : tokens.paper,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.75,
                          height: '100%',
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                          <Typography sx={{ fontWeight: 700, fontSize: '1rem', minWidth: 0 }}>{branch.publicTitle || branch.name}</Typography>
                          {branch.isCurrent && <StatusPill label="Ten oddział" tone="plum" size="small" noDot />}
                        </Stack>
                        <Stack direction="row" gap={1} alignItems="flex-start" sx={{ color: tokens.inkSoft }}>
                          <LocationOnOutlinedIcon sx={{ fontSize: 18, mt: '2px', color: tokens.muted }} />
                          <Typography sx={{ fontSize: '0.875rem' }}>{branch.publicAddress}</Typography>
                        </Stack>
                        {branch.publicPhone && (
                          <Stack direction="row" gap={1} alignItems="center" sx={{ color: tokens.inkSoft }}>
                            <PhoneOutlinedIcon sx={{ fontSize: 18, color: tokens.muted }} />
                            <Typography sx={{ fontSize: '0.875rem' }}>{branch.publicPhone}</Typography>
                          </Stack>
                        )}
                        <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 'auto', pt: 1 }}>
                          {branch.allowOnlineBooking && branch.slug && (
                            <Button component={Link} to={`/s/${branch.slug}/rezerwacja`} size="small" variant="outlined">
                              Rezerwuj w tym oddziale
                            </Button>
                          )}
                          {!branch.isCurrent && branch.slug && (
                            <Button component={Link} to={`/s/${branch.slug}`} size="small" variant="text">
                              Zobacz wizytówkę
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    ))}
                  </Box>
                </SectionCard>
              </Box>
            )}
          </Stack>

          <Stack gap={{ xs: 2, md: 3 }} sx={{ minWidth: 0, position: { md: 'sticky' }, top: { md: 80 } }}>
            {salon.allowOnlineBooking && (
              <Paper id="rezerwacja" sx={{ p: { xs: 2, md: 2.5 }, scrollMarginTop: 72 }}>
                <Typography component="h2" sx={{ fontSize: '1.0625rem', fontWeight: 700 }}>Rezerwacja online</Typography>
                <Typography sx={{ mt: 0.5, color: tokens.inkSoft, fontSize: '0.9375rem' }}>
                  Wybierz usługę, specjalistę i dogodny termin — rezerwacja zajmie kilka minut.
                </Typography>
                <Stack gap={1} sx={{ mt: 2 }}>
                  <Button size="large" variant="contained" fullWidth onClick={() => navigate(bookingUrl)}>
                    Zarezerwuj wizytę
                  </Button>
                  {canCall && (
                    <Button variant="outlined" fullWidth startIcon={<PhoneOutlinedIcon />} href={`tel:${phone}`}>
                      {phone}
                    </Button>
                  )}
                </Stack>
              </Paper>
            )}

            <Box id="kontakt" sx={{ scrollMarginTop: 72 }}>
              <SectionCard title="Kontakt" contentSx={{ pt: 0.75, pb: 1.25 }}>
                {phone && (
                  <ContactRow icon={<PhoneOutlinedIcon />}>
                    <MuiLink href={`tel:${phone}`} sx={{ color: tokens.ink, fontWeight: 600 }}>{phone}</MuiLink>
                  </ContactRow>
                )}
                {email && (
                  <ContactRow icon={<AlternateEmailOutlinedIcon />}>
                    <MuiLink href={`mailto:${email}`} sx={{ color: tokens.ink, fontWeight: 600 }}>{email}</MuiLink>
                  </ContactRow>
                )}
                {address && <ContactRow icon={<LocationOnOutlinedIcon />}>{address}</ContactRow>}
                {!phone && !email && !address && (
                  <Typography sx={{ color: tokens.muted, py: 1.25 }}>Salon nie podał danych kontaktowych.</Typography>
                )}
              </SectionCard>
            </Box>

            <Box id="godziny" sx={{ scrollMarginTop: 72 }}>
              <SectionCard title="Godziny otwarcia" contentSx={{ pt: 1, pb: 1.5 }}>
                {hoursByDay.length ? (
                  hoursByDay.map((item) => {
                    const isToday = item.dayOfWeek === todayIdx;
                    return (
                      <Stack
                        key={item.id}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={2}
                        sx={{ py: 0.9, '&:not(:last-of-type)': { borderBottom: `1px solid ${tokens.line}` } }}
                      >
                        <Stack direction="row" alignItems="center" gap={1}>
                          <Typography sx={{ fontSize: '0.9375rem', fontWeight: isToday ? 700 : 500 }}>{DAY_LABELS[item.dayOfWeek]}</Typography>
                          {isToday && <StatusPill label="dziś" tone="plum" size="small" noDot />}
                        </Stack>
                        <Typography
                          sx={{
                            fontSize: '0.9375rem',
                            fontVariantNumeric: 'tabular-nums',
                            fontWeight: isToday ? 700 : 500,
                            color: item.isClosed ? tokens.muted : tokens.ink,
                          }}
                        >
                          {hoursText(item)}
                        </Typography>
                      </Stack>
                    );
                  })
                ) : (
                  <Typography sx={{ color: tokens.muted, py: 1 }}>Salon nie opublikował godzin otwarcia.</Typography>
                )}
              </SectionCard>
            </Box>
          </Stack>
        </Box>
      </Container>

      <Box component="footer" sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="caption">
          Wizytówka i rezerwacje online w{' '}
          <MuiLink component={Link} to="/" sx={{ fontSize: 'inherit' }}>Visitio</MuiLink>
        </Typography>
      </Box>

      {salon.allowOnlineBooking && (
        <Box
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 20,
            px: 2,
            pt: 1.5,
            pb: 'calc(12px + env(safe-area-inset-bottom))',
            bgcolor: alpha(tokens.paper, 0.96),
            backdropFilter: 'blur(10px)',
            borderTop: `1px solid ${tokens.line}`,
          }}
        >
          <Button size="large" variant="contained" fullWidth startIcon={<EventAvailableOutlinedIcon />} onClick={() => navigate(bookingUrl)}>
            Zarezerwuj wizytę
          </Button>
        </Box>
      )}
    </Box>
  );
};
